import { useTranslation } from 'react-i18next';

import Avatar from '@/component-library/primitives/Avatar/Avatar';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';

interface MemberProfileSideMenuProps {
  memberData: MemberOverview | undefined;
  /** Same email-filtered logs as the overview activity chart (workspace logs are not on memberData). */
  activityLogs?: ActivityLog[];
}

function activityTimeMs(activity: ActivityLog): number {
  const t = activity.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t) && t > 0) return t;
  if (activity.createdAt) {
    const ms = new Date(activity.createdAt).getTime();
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return 0;
}

function maxActivityTimeMs(logs: ActivityLog[]): number {
  return logs.reduce((acc, log) => Math.max(acc, activityTimeMs(log)), 0);
}

function engagementBadgeClass(level: string | undefined): string {
  switch ((level || '').toLowerCase()) {
    case 'high':
      return 'bg-custom-green';
    case 'medium':
      return 'bg-custom-blue';
    case 'low':
      return 'bg-custom-yellow';
    case 'inactive':
      return 'bg-custom-darkSilver';
    default:
      return 'bg-custom-darkSilver';
  }
}

function MemberProfileSideMenu({ memberData, activityLogs }: MemberProfileSideMenuProps) {
  const { t } = useTranslation();

  const engagementLevel =
    memberData?.engagement ?? memberData?.parsedChurnRiskData?.engagementLevel ?? '';

  return (
    <div className="pb-8">
      {/* <div className="max-w-[960px] flex justify-between items-end mx-auto"> */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 flex items-center gap-4">
          <Avatar initials={memberData?.name} size="big" className="!rounded-full" />

          <div>
            <h2 className="text-custom-black text-2xl font-bold mb-1">{memberData?.name}</h2>

            <div className="text-sm text-custom-darkSilver">
              <span>{t('MemberProfilePage.signedUpLabel')}&nbsp;</span>
              <span>
                {memberData?.createdAt
                  ? new Date(memberData.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                    })
                  : '-'}
              </span>
            </div>

            <div className="text-sm text-custom-darkSilver">
              <span>{t('MemberProfilePage.lastVisitedLabel')}&nbsp;</span>
              <span>
                {(() => {
                  const formatVisited = (d: Date) =>
                    d.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });

                  const fromWorkspace = activityLogs?.length ? maxActivityTimeMs(activityLogs) : 0;
                  if (fromWorkspace > 0) {
                    return formatVisited(new Date(fromWorkspace));
                  }

                  const lastActivity = memberData?.lastActivity;
                  if (lastActivity) {
                    return formatVisited(new Date(lastActivity));
                  }

                  const metrics = memberData?.parsedChurnRiskData?.activityMetrics;
                  if (
                    metrics &&
                    metrics.daysSinceLastActivity >= 0 &&
                    metrics.lastActivityDate?.trim()
                  ) {
                    return formatVisited(new Date(metrics.lastActivityDate));
                  }

                  return 'Never';
                })()}
              </span>
            </div>
          </div>
        </div>

        <ul className="col-span-4 p-4 bg-custom-silver rounded-lg ">
          <li className="flex justify-between items-center text-sm mb-2">
            <span className="text-custom-darkSilver">{t('MemberProfilePage.emailLabel')}</span>
            <span className="font-semibold text-custom-black">{memberData?.email || '-'}</span>
          </li>
          <li className="flex justify-between items-center text-sm mb-2">
            <span className="text-custom-darkSilver">{t('MemberProfilePage.phoneLabel')}</span>
            <span className="font-semibold text-custom-black">{memberData?.phoneNumber || '-'}</span>
          </li>
          <li className="mb-2 flex items-center justify-between text-sm">
            <span className="text-custom-darkSilver">Plan</span>
            <span className="font-semibold text-custom-blue">{memberData?.subscriptionPlan || '-'}</span>
          </li>
          <li className="flex items-center justify-between text-sm">
            <span className="text-custom-darkSilver">Engagement</span>
            {engagementLevel ? (
              <span
                className={`text-xs px-2 py-0.5 rounded-lg text-white capitalize ${engagementBadgeClass(engagementLevel)}`}
              >
                {engagementLevel}
              </span>
            ) : (
              <span className="font-semibold text-custom-black">—</span>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}

export default MemberProfileSideMenu;
